/**
 * CognixToolList
 *
 * Displays the user's tool library. Each tool references an existing datasource
 * (DB or API) and defines a query / API endpoint configuration.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { cognixToolService } from '../services/cognixToolService';
import { TrashIcon, PencilIcon, PlusIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import CognixToolBuilder from './CognixToolBuilder';

const CognixToolList = ({ refreshTrigger }) => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingTool, setEditingTool] = useState(null);   // null = create, object = edit
  const [localRefresh, setLocalRefresh] = useState(0);

  const loadTools = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await cognixToolService.listTools();
      setTools(data?.tools || []);
    } catch (err) {
      console.error('Failed to load tools:', err);
      setError(err.message || 'Failed to load tools');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTools();
  }, [loadTools, refreshTrigger, localRefresh]);

  const handleDelete = async (toolUuid, toolName) => {
    if (!window.confirm(`Delete tool "${toolName}"?`)) return;
    try {
      setDeleteLoading(toolUuid);
      await cognixToolService.deleteTool(toolUuid);
      setLocalRefresh(r => r + 1);
    } catch (err) {
      alert('Failed to delete: ' + (err.message || err));
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleEdit = (tool) => {
    setEditingTool(tool);
    setShowBuilder(true);
  };

  const handleBuilderClose = () => {
    setShowBuilder(false);
    setEditingTool(null);
  };

  const handleBuilderSuccess = () => {
    setShowBuilder(false);
    setEditingTool(null);
    setLocalRefresh(r => r + 1);
  };

  const getTypeBadge = (datasourceType) => {
    if (datasourceType === 'DB') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
          DB
        </span>
      );
    }
    if (datasourceType === 'EMAIL') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
          EMAIL
        </span>
      );
    }
    if (datasourceType === 'KNOWLEDGE_BASE') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          KB
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
        API
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading tools...</div>
      </div>
    );
  }

  return (
    <div className="cognix-tool-list">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Tool Library</h2>
          <p className="text-sm text-gray-500">
            Reusable tools built on your datasources — assign them to Cognix Agents
          </p>
        </div>
        <button
          onClick={() => { setEditingTool(null); setShowBuilder(true); }}
          className="inline-flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Create Tool
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Empty State */}
      {tools.length === 0 && !error && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Tools Yet</h3>
          <p className="text-gray-600 mb-4">
            Create tools on top of your existing datasources to use in Cognix Agents
          </p>
          <button
            onClick={() => { setEditingTool(null); setShowBuilder(true); }}
            className="inline-flex items-center gap-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4" />
            Create your first tool
          </button>
        </div>
      )}

      {/* Tools Table */}
      {tools.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tool Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Datasource
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parameters
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tools.map(tool => {
                const params = tool.tool_config?.Parameter || [];
                return (
                  <tr key={tool.tool_uuid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{tool.tool_name}</div>
                      <div className="text-xs text-gray-400 font-mono">{tool.tool_uuid.slice(0, 8)}…</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getTypeBadge(tool.datasource_type)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{tool.datasource_name || `ID: ${tool.datasource_id}`}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-xs truncate" title={tool.tool_description}>
                        {tool.tool_description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {params.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {params.map(p => (
                            <span key={p} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-gray-100 text-gray-700">
                              {p}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">none</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {tool.created_at ? new Date(tool.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(tool)}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit tool"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(tool.tool_uuid, tool.tool_name)}
                          disabled={deleteLoading === tool.tool_uuid}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Delete tool"
                        >
                          {deleteLoading === tool.tool_uuid ? (
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Tool Builder Modal */}
      {showBuilder && (
        <CognixToolBuilder
          existingTool={editingTool}
          onClose={handleBuilderClose}
          onSuccess={handleBuilderSuccess}
        />
      )}
    </div>
  );
};

export default CognixToolList;
